// src/pages/LocationsEditPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { branchApi } from "../Apis/Api";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Polygon,
  useMap,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import LoadingComponent from "../components/ui/LoadingComponent";
import CustomSelect from "../components/common/CustomSelect";
import Breadcrumb from "../components/Breadcrumb";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

// Search Control Component
const SearchControl = ({ onLocationFound }) => {
  const map = useMap();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const searchLocation = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setError(null);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          query
        )}&limit=5`
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const results = await response.json();
      setSearchResults(results);
      if (results.length === 0) {
      }
    } catch (error) {
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    map.setView([lat, lon], 15);
    const marker = L.marker([lat, lon]).addTo(map);
    marker.bindPopup(result.display_name).openPopup();
    setSearchQuery("");
    setSearchResults([]);
    setError(null);
    if (onLocationFound) {
      onLocationFound({ lat, lng: lon, name: result.display_name });
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => searchLocation(searchQuery), 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div
      className="leaflet-top leaflet-right"
      style={{ margin: "10px 50px", zIndex: 1000 }}
    >
      <div
        className="leaflet-control"
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ position: "relative", width: "300px" }}>
          <input
            type="text"
            placeholder="🔍 Search location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 40px 12px 16px",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              backgroundColor: "#fff",
              color: "#333",
            }}
            aria-label="Search location"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSearchResults([]);
                setError(null);
              }}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
                color: "#666",
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
          {isSearching && (
            <div
              style={{
                padding: "12px",
                fontSize: "12px",
                color: "#666",
                backgroundColor: "#f8f9fa",
              }}
            >
              🔄 Searching...
            </div>
          )}
          {error && (
            <div
              style={{
                padding: "12px",
                fontSize: "12px",
                color: "#dc3545",
                backgroundColor: "#f8f9fa",
              }}
            >
              {error}
            </div>
          )}
          {searchResults.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                borderRadius: "0 0 8px 8px",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                maxHeight: "250px",
                overflowY: "auto",
                zIndex: 1001,
                marginTop: "2px",
              }}
            >
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  onClick={() => handleSearchSelect(result)}
                  style={{
                    padding: "12px 16px",
                    cursor: "pointer",
                    borderBottom:
                      index < searchResults.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "white")
                  }
                  role="option"
                  aria-selected={false}
                >
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: "13px",
                      color: "#333",
                    }}
                  >
                    📍 {result.name || result.display_name.split(",")[0]}
                  </div>
                  <div
                    style={{
                      color: "#666",
                      fontSize: "11px",
                      lineHeight: "1.3",
                    }}
                  >
                    {result.display_name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// MapActions Component
const MapActions = ({ polygons }) => {
  const map = useMap();
  useEffect(() => {
    if (polygons && polygons.length > 0) {
      map.whenReady(() => {
        const leafletPolygons = polygons.map((p) => [p.lat, p.lng]);
        const bounds = L.polygon(leafletPolygons).getBounds();
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
      });
    }
  }, [polygons, map]);
  return null;
};

// Main LocationsEditPage Component
const LocationsEditPage = () => {
  const location = useLocation();
  const { locationData } = location.state || {};
  const navigate = useNavigate();
  const featureGroupRef = useRef();
  const mapRef = useRef();

  const [formData, setFormData] = useState({
    location_id: "",
    branch: null,
    location_name: "",
    location_code: "",
    notes: "",
    is_active: null,
    polygons: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [mapCenter, setMapCenter] = useState([51.505, -0.09]);

  const statusOptions = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

  useEffect(() => {
    L.drawLocal.draw.toolbar.buttons.polygon = "Draw a location polygon";
    L.Draw.Polygon.prototype._getTooltipText = function () {
      return {
        text: this._initialLabelText || "Click to start drawing a polygon",
      };
    };
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        const response = await branchApi.getClientBranches({
          want_pagination: 0,
          sort_by: "branch_name",
          sort_order: "asc",
        });
        const branchData = response.data.map((branch) => ({
          value: branch.branch_id,
          label: branch.branch_name,
        }));
        setBranches(branchData);
        handleApiResponse(response, "Branches loaded", "GET");
      } catch (error) {
        handleApiResponse(error, "Failed to load Branches", "GET");
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    if (locationData) {
      const polygons = locationData.polygons || [];
      setFormData({
        location_id: locationData.location_id || "",
        branch: locationData.branch_id
          ? { value: locationData.branch_id, label: locationData.branch_name }
          : null,
        location_name: locationData.location_name || "",
        location_code: locationData.location_code || "",
        notes: locationData.notes || "",
         is_active:
    locationData.is_active === "Active" || locationData.is_active === true
      ? { value: "true", label: "Active" }  // ✅ String value use karein
      : { value: "false", label: "Inactive" }, // ✅ String value use karein
        polygons,
      });
      if (polygons.length > 0) {
        const bounds = L.polygon(
          polygons.map((p) => [p.lat, p.lng])
        ).getBounds();
        setMapCenter(bounds.getCenter());
      }
    } else {
      handleCustomResponse("error", "No location data found");
      navigate(-1);
    }
  }, [locationData, navigate]);

  useEffect(() => {
    if (navigator.geolocation && formData.polygons.length === 0) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapCenter([latitude, longitude]);
        },
        (error) => {
          handleCustomResponse(
            "info",
            "Default location used. Location enable for better experience."
          );
        }
      );
    }
  }, [formData.polygons]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBranchChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, branch: selectedOption }));
    if (errors.branch) {
      setErrors((prev) => ({ ...prev, branch: "" }));
    }
  };

  const handleStatusChange = (selectedOption) => {
  // Convert string value to boolean
  const booleanValue = selectedOption ? selectedOption.value === "true" : null;
  
  setFormData((prev) => ({ 
    ...prev, 
    is_active: selectedOption 
  }));
  
  if (errors.is_active) {
    setErrors((prev) => ({ ...prev, is_active: "" }));
  }
};

  const handlePolygonCreated = (e) => {
    const layer = e.layer;
    const latlngs = layer.getLatLngs()[0].map((point) => ({
      lat: point.lat,
      lng: point.lng,
    }));
    setFormData((prev) => ({ ...prev, polygons: latlngs }));
    if (mapRef.current) {
      const bounds = L.polygon(latlngs.map((p) => [p.lat, p.lng])).getBounds();
      mapRef.current.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 });
    }
  };

  const handleLocationFound = (foundLocation) => {
    setMapCenter([foundLocation.lat, foundLocation.lng]);
    if (!formData.location_name) {
      setFormData((prev) => ({ ...prev, location_name: foundLocation.name }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.location_name.trim())
      newErrors.location_name = "Location name is required";
    if (!formData.location_code.trim())
      newErrors.location_code = "Location code is required";
    if (!formData.branch) newErrors.branch = "Selecting a branch is required";
    if (!formData.is_active)
      newErrors.is_active = "Selecting a status is required";
    if (formData.polygons.length === 0)
      newErrors.polygons = "Draw a polygon on the map";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
       const payload = {
      branch_id: formData.branch?.value,
      location_name: formData.location_name,
      location_code: formData.location_code,
      notes: formData.notes.trim() || null,
      // Convert string value to boolean for API
      is_active: formData.is_active?.value === "true",
      polygons: formData.polygons,
    };
      const response = await branchApi.updateBranchLocation(
        formData.location_id,
        payload
      );
      handleApiResponse(response, "Location has been updated", "PUT");
      navigate(-1);
    } catch (err) {
      handleApiResponse(err, "Location has not been updated", "PUT");
    } finally {
      setLoading(false);
    }
  };

  if (loadingBranches) {
    return (
      <>
        <Breadcrumb title="Edit Location" />
        <LoadingComponent />
      </>
    );
  }

  return (
    <>
      <Breadcrumb title="Edit Location" />
      <div className="card shadow-sm border-0 rounded p-5">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="branch" className="form-label fw-semibold">
                Branch Name <span className="text-danger">*</span>
              </label>
              <CustomSelect
                name="branch"
                value={formData.branch}
                onChange={handleBranchChange}
                options={branches}
                placeholder="Select Branch"
                isDisabled={loadingBranches}
                isSearchable
                errors={errors.branch}
                addNewLabel="Add New Branch"
                addNewRoute="/branch-create"
              />
              {errors.branch && (
                <div className="text-danger small mt-1">{errors.branch}</div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="location_name" className="form-label fw-semibold">
                Location Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="location_name"
                name="location_name"
                value={formData.location_name}
                onChange={handleChange}
                placeholder="Location Name"
                className={`form-control rounded ${
                  errors.location_name ? "border-danger" : ""
                }`}
              />
              {errors.location_name && (
                <div className="text-danger small mt-1">
                  {errors.location_name}
                </div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="location_code" className="form-label fw-semibold">
                Location Code
              </label>
              <input
                type="text"
                id="location_code"
                name="location_code"
                value={formData.location_code}
                onChange={handleChange}
                placeholder="Location Code "
                className={`form-control rounded ${
                  errors.location_code ? "border-danger" : ""
                }`}
                disabled
              />
              {errors.location_code && (
                <div className="text-danger small mt-1">
                  {errors.location_code}
                </div>
              )}
            </div>
           <div className="col-md-6 mb-3">
  <label htmlFor="status" className="form-label fw-semibold">
    Status <span className="text-danger">*</span>
  </label>
  <CustomSelect
    name="status"
    value={formData.is_active}
    onChange={handleStatusChange}
    options={statusOptions}
    placeholder="Select status"
    isSearchable={false}
    errors={errors.is_active}
  />
  {errors.is_active && (
    <div className="text-danger small mt-1">{errors.is_active}</div>
  )}
</div>
            {/* <div className="col-12 mb-3">
              <label htmlFor="notes" className="form-label fw-semibold">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Notes ..."
                className="form-control rounded"
                rows={4}
              />
            </div> */}
            <div
              className="col-12 mb-3"
              style={{ position: "relative", zIndex: 1 }}
            >
              <label className="form-label fw-semibold">
                Search & Edit Location Polygon{" "}
                <span className="text-danger">*</span>
                <small className="text-neutral-900 d-block">
                  Use the search box on the map and edit the polygon.
                </small>
              </label>
              <MapContainer
                center={mapCenter}
                zoom={13}
                whenCreated={(map) => (mapRef.current = map)}
                style={{ height: "400px", width: "100%", borderRadius: "8px" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <SearchControl onLocationFound={handleLocationFound} />
                <FeatureGroup ref={featureGroupRef}>
                  <EditControl
                    position="topright"
                    onCreated={handlePolygonCreated}
                    draw={{
                      polygon: {
                        shapeOptions: {
                          color: "#e50808ff",
                          weight: 3,
                          opacity: 0.8,
                          fillColor: "#e50808ff",
                          fillOpacity: 0.2,
                        },
                      },
                      polyline: false,
                      rectangle: false,
                      circle: false,
                      marker: false,
                      circlemarker: false,
                    }}
                    edit={{ remove: true }}
                  />
                  {formData.polygons.length > 0 && (
                    <Polygon
                      positions={formData.polygons.map((p) => [p.lat, p.lng])}
                      pathOptions={{
                        color: "#e50808ff",
                        weight: 3,
                        opacity: 0.8,
                        fillColor: "#e50808ff",
                        fillOpacity: 0.2,
                      }}
                    />
                  )}
                </FeatureGroup>
                <MapActions polygons={formData.polygons} />
              </MapContainer>
              {errors.polygons && (
                <div className="text-danger small mt-1">{errors.polygons}</div>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
            <CancelButtonForm />
            <UpdateButtonForm disabled={loading} />
          </div>
        </form>
      </div>
    </>
  );
};

export default LocationsEditPage;
